// suggestionInterceptor.ts
//
// ProseMirror plugin that, while Suggesting mode is ON, rewrites a user's edit
// into a tracked-change suggestion mark instead of a direct change. The marks
// live in the doc (the single source of truth — synced via Yjs Collaboration);
// `localSuggestions` is derived by scanning the doc (see scanSuggestions), so
// this plugin stays a PURE transaction transform with no external side effects.
//
// MVP coverage: single-ReplaceStep edits (typing, backspace/delete, selection
// replace, single paste) — the overwhelming majority of real edits. Multi-step
// edits fall through unchanged (documented edge case).
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey, EditorState } from "prosemirror-state";
import { ReplaceStep } from "prosemirror-transform";
import { Mark, MarkType } from "prosemirror-model";
import { ySyncPluginKey } from "@tiptap/y-tiptap";
import { v4 as uuidv4 } from "uuid";

export interface SuggestionAuthor {
  id: string;
  name: string;
  color: string;
}

export interface SuggestionContext {
  suggesting: boolean;
  author: SuggestionAuthor;
}

// Meta flag marking a transaction WE generated — the interceptor's own rewrite
// OR an accept/reject op (see suggestionOps) — so it is never re-processed into a
// fresh suggestion. Exported so suggestionOps can tag accept/reject the same way.
export const SUGGESTION_INTERNAL_META = "suggestion:internal";
export const suggestionInterceptorKey = new PluginKey("suggestionInterceptor");

/** The suggestion mark on a node, if any. */
function suggMark(marks: readonly Mark[], markType: MarkType): Mark | null {
  return marks.find((m) => m.type === markType) ?? null;
}

/** True iff every text position in [from,to) carries this author's INSERT mark. */
function isRangeFullyOwnInsert(
  state: EditorState,
  from: number,
  to: number,
  markType: MarkType,
  authorId: string,
): boolean {
  if (to <= from) return false;
  let ok = true;
  state.doc.nodesBetween(from, to, (node) => {
    if (!node.isText) return;
    const m = suggMark(node.marks, markType);
    if (!m || m.attrs.type !== "insert" || m.attrs.authorId !== authorId) ok = false;
  });
  return ok;
}

/** Id of this author's INSERT suggestion mark immediately before `pos`, if the
 *  inserted text should extend it (so a run of keystrokes stays one suggestion). */
function adjacentOwnInsertId(
  state: EditorState,
  pos: number,
  markType: MarkType,
  authorId: string,
): string | null {
  if (pos <= 0) return null;
  const $pos = state.doc.resolve(pos);
  const before = $pos.nodeBefore;
  if (!before || !before.isText) return null;
  const m = suggMark(before.marks, markType);
  if (m && m.attrs.type === "insert" && m.attrs.authorId === authorId) {
    return m.attrs.id as string;
  }
  return null;
}

export const createSuggestionInterceptor = (getContext: () => SuggestionContext) => {
  return new Plugin({
    key: suggestionInterceptorKey,
    appendTransaction(trs, oldState, newState) {
      const ctx = getContext();
      if (!ctx.suggesting) return null;

      // Only rewrite the LOCAL user's own direct edits. Skip:
      //  - our own internal ops (interceptor rewrites + accept/reject), and
      //  - remote Yjs sync transactions (ySyncPluginKey meta) — suggestion marks
      //    already propagate via Yjs, so a remote client must never re-wrap them
      //    (that produced cascading suggestions when a peer accepted/edited).
      const userTr = trs.find(
        (t) =>
          t.docChanged &&
          !t.getMeta(SUGGESTION_INTERNAL_META) &&
          !t.getMeta(ySyncPluginKey),
      );
      if (!userTr) return null;
      if (userTr.steps.length !== 1) return null; // MVP: single-step edits only
      const step = userTr.steps[0];
      if (!(step instanceof ReplaceStep)) return null;

      const markType = newState.schema.marks.suggestion as MarkType | undefined;
      if (!markType) return null;

      const from = step.from;
      const to = step.to;
      const insertedSize = step.slice.size;
      const deletedSize = to - from;
      if (insertedSize === 0 && deletedSize === 0) return null;

      const a = ctx.author;
      const mk = (type: "insert" | "delete", id: string) =>
        markType.create({ id, authorId: a.id, authorName: a.name, type, color: a.color });

      const tr = newState.tr.setMeta(SUGGESTION_INTERNAL_META, true);

      // DELETE side: the user removed oldState[from,to].
      let insAnchor = from; // where the inserted content now begins
      if (deletedSize > 0) {
        if (isRangeFullyOwnInsert(oldState, from, to, markType, a.id)) {
          // Deleting your own still-pending insertion: let the real deletion stand.
          insAnchor = from;
        } else {
          // Restore the removed content (gone in newState) as a delete suggestion.
          const removed = oldState.doc.slice(from, to);
          tr.insert(from, removed.content);
          const dEnd = from + deletedSize;
          tr.removeMark(from, dEnd, markType);
          tr.addMark(from, dEnd, mk("delete", uuidv4()));
          insAnchor = dEnd;
        }
      }

      // INSERT side: the inserted content occupies [insAnchor, insAnchor+insertedSize].
      if (insertedSize > 0) {
        const insFrom = insAnchor;
        const insTo = insAnchor + insertedSize;
        const adjId = deletedSize > 0 ? null : adjacentOwnInsertId(oldState, from, markType, a.id);
        tr.removeMark(insFrom, insTo, markType);
        tr.addMark(insFrom, insTo, mk("insert", adjId || uuidv4()));
      }

      return tr.steps.length ? tr : null;
    },
  });
};

/** TipTap wrapper so the interceptor can be added to the editor's extension list.
 *  `getContext` reads live refs (suggesting flag + author), so toggling Suggesting
 *  mode never re-creates the editor. */
export const SuggestionInterceptorExtension = Extension.create<{
  getContext: () => SuggestionContext;
}>({
  name: "suggestionInterceptor",
  addOptions() {
    return {
      getContext: () => ({
        suggesting: false,
        author: { id: "", name: "", color: "#2c1561" },
      }),
    };
  },
  addProseMirrorPlugins() {
    return [createSuggestionInterceptor(this.options.getContext)];
  },
});
